import { ReactNode, useEffect } from 'react';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

import { usePermissionCheck } from '../utils/permissions-utils';
import { useAuthStore } from '../store/auth.store';
import type { PermissionModule, PermissionAction } from '../types/permissions';

interface ModulePermissionGuardProps {
  children: ReactNode;
  module: PermissionModule;
  action?: PermissionAction;
  fallbackPath?: string;
  showForbidden?: boolean;
  renderFallback?: () => ReactNode;
}

export default function ModulePermissionGuard({
  children,
  module,
  action,
  fallbackPath = paths.root,
  showForbidden = true,
  renderFallback,
}: ModulePermissionGuardProps) {
  const router = useRouter();
  const { checkModule } = usePermissionCheck();
  const { isAuthenticated } = useAuthStore();

  // Re-avalia permissões (token refresh é automático via interceptor)
  const hasModuleAccess = checkModule(module, action);

  useEffect(() => {
    // Se não está autenticado, redireciona para login
    if (!isAuthenticated) {
      router.replace(paths.auth.login);
      return;
    }

    if (!hasModuleAccess && !renderFallback) {
      if (showForbidden) {
        router.replace(paths.error.forbidden);
      } else {
        router.replace(fallbackPath);
      }
    }
  }, [hasModuleAccess, renderFallback, showForbidden, router, fallbackPath, isAuthenticated]);

  if (!hasModuleAccess) {
    if (renderFallback) {
      return <>{renderFallback()}</>;
    }

    return null;
  }

  return <>{children}</>;
}
